/**
 * SC-01: 営業ダッシュボード
 *
 * 全体の営業状況を俯瞰する画面。KPIや重要な情報をサマリー表示
 *
 * @created 2025-11-16
 * @updated 2025-11-21 UI改善・アラートセクション追加・全体表示に変更
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  IconButton,
  Tooltip,
  Chip,
  Divider,
  Collapse,
} from '@mui/material';
import {
  TrendingUp,
  Assignment,
  AttachMoney,
  Warning,
  ErrorOutline,
  Schedule,
  CheckCircle,
  Cancel,
  ExpandMore,
  ExpandLess,
  Business,
  Person,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { salesKpiApi, salesCasesApi } from '../../services/salesApi';
import { formatCurrencyWithUnit } from '../../utils/formatNumber';
import type { KpiDashboardData, SalesCase, PipelineData, StagnationPhase, LostReasonData, YearlySummaryData, BusinessCategoryHeatmapItem, AmountChangeAlert, AccountStatisticsData, SalesAssignee } from '../../services/salesApi';
import PhaseTransitionSankey from '../../components/Sales/PhaseTransitionSankey';
import { extractErrorMessage } from '../../utils/errorHandler';
import { formatDateJST } from '../../utils/dateUtils';

const SalesDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dashboardData, setDashboardData] = useState<KpiDashboardData | null>(null);
  const [upcomingCases, setUpcomingCases] = useState<SalesCase[]>([]);
  const [stagnationAlertCases, setStagnationAlertCases] = useState<SalesCase[]>([]);
  const [actionOverdueCases, setActionOverdueCases] = useState<SalesCase[]>([]);
  const [actionUrgentCases, setActionUrgentCases] = useState<SalesCase[]>([]);
  const [actionUpcomingCases, setActionUpcomingCases] = useState<SalesCase[]>([]);
  const [amountChangeAlerts, setAmountChangeAlerts] = useState<AmountChangeAlert[]>([]);
  const [pipelineData, setPipelineData] = useState<PipelineData | null>(null);
  const [stagnationData, setStagnationData] = useState<StagnationPhase[]>([]);
  const [lostReasonData, setLostReasonData] = useState<LostReasonData | null>(null);
  const [lostReasonYear, setLostReasonYear] = useState<number | null>(null);
  const [yearlySummaryData, setYearlySummaryData] = useState<YearlySummaryData | null>(null);
  const [heatmapData, setHeatmapData] = useState<BusinessCategoryHeatmapItem[]>([]);
  const [accountStatsData, setAccountStatsData] = useState<AccountStatisticsData | null>(null);
  const [accountStatsYear, setAccountStatsYear] = useState<number>(new Date().getFullYear());
  const [expandedAccount, setExpandedAccount] = useState<number | null>(null);
  const [expandedCase, setExpandedCase] = useState<number | null>(null);
  // アラートカードの展開状態
  const [expandedAlerts, setExpandedAlerts] = useState<{
    amountChange: boolean;
    overdue: boolean;
    urgent: boolean;
    upcoming: boolean;
    stagnation: boolean;
  }>({
    amountChange: false,
    overdue: false,
    urgent: false,
    upcoming: false,
    stagnation: false,
  });
  const currentYear = new Date().getFullYear();
  const fiscalYears = [currentYear - 1, currentYear, currentYear + 1, currentYear + 2];
  const [selectedFiscalYear, setSelectedFiscalYear] = useState<number>(currentYear);

  // 営業担当者フィルタ
  const [assignees, setAssignees] = useState<SalesAssignee[]>([]);
  const [selectedAssignee, setSelectedAssignee] = useState<string>('');

  // 営業担当者リスト取得
  const fetchAssignees = async () => {
    try {
      const assigneesList = await salesCasesApi.searchAssignees('');
      setAssignees(assigneesList || []);
    } catch (err) {
      console.error('担当者リスト取得エラー:', err);
    }
  };

  useEffect(() => {
    fetchAssignees();
  }, []);

  useEffect(() => {
    if (user) {
      fetchDashboardData();
      fetchUpcomingCases();
      fetchAnalysisData();
      fetchAlertCases();
      fetchAccountStats(accountStatsYear);
    }
  }, [user, selectedAssignee]);

  // 顧客別統計取得
  const fetchAccountStats = async (year: number) => {
    try {
      const params: { fiscal_year?: number; employee_id?: string } = { fiscal_year: year };
      if (selectedAssignee) {
        params.employee_id = selectedAssignee;
      }
      const response = await salesKpiApi.getAccountStatistics(params);
      if (response.data.success) {
        setAccountStatsData(response.data.data);
      }
    } catch (err) {
      console.error('顧客別統計取得エラー:', err);
    }
  };

  // 顧客別統計の年度変更
  const handleAccountStatsYearChange = (year: number) => {
    setAccountStatsYear(year);
    fetchAccountStats(year);
    setExpandedAccount(null);
    setExpandedCase(null);
  };

  const fetchAnalysisData = async (year?: number | null) => {
    try {
      const employeeFilter = selectedAssignee ? { employee_id: selectedAssignee } : {};
      const lostReasonsParams: { year?: number; employee_id?: string } = { ...employeeFilter };
      if (year) {
        lostReasonsParams.year = year;
      }
      
      const [pipelineRes, stagnationRes, lostRes, heatmapRes] = await Promise.all([
        salesKpiApi.getPipeline(employeeFilter),
        salesKpiApi.getStagnation(employeeFilter),
        salesKpiApi.getLostReasons(lostReasonsParams),
        salesKpiApi.getBusinessCategoryHeatmap(employeeFilter),
      ]);
      if (pipelineRes.data.success) setPipelineData(pipelineRes.data.data);
      if (stagnationRes.data.success) setStagnationData(stagnationRes.data.data as StagnationPhase[]);
      if (lostRes.data.success) setLostReasonData(lostRes.data.data);
      if (heatmapRes.data.success) setHeatmapData(heatmapRes.data.data);
    } catch (err) {
      console.error('分析データ取得エラー:', err);
    }
  };

  const handleYearChange = (year: number | null) => {
    setLostReasonYear(year);
    fetchAnalysisData(year);
  };

  // 年度内期待売上額の年度変更
  const handleFiscalYearChange = async (year: number) => {
    setSelectedFiscalYear(year);
    try {
      const params: { year?: number; employee_id?: string } = { year };
      if (selectedAssignee) {
        params.employee_id = selectedAssignee;
      }
      const yearlySummaryRes = await salesKpiApi.getYearlySummary(params);
      if (yearlySummaryRes.data.success) {
        setYearlySummaryData(yearlySummaryRes.data.data);
      }
    } catch (err) {
      console.error('年度サマリー取得エラー:', err);
    }
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const fiscalYear = 2026; // 案件データが2026年度に集中

      const dashboardParams: { employee_id?: string } = {};
      if (selectedAssignee) {
        dashboardParams.employee_id = selectedAssignee;
      }

      const yearlySummaryParams: { year?: number; employee_id?: string } = { year: fiscalYear };
      if (selectedAssignee) {
        yearlySummaryParams.employee_id = selectedAssignee;
      }

      const [dashboardRes, yearlySummaryRes] = await Promise.all([
        salesKpiApi.getKpiDashboard(dashboardParams),
        salesKpiApi.getYearlySummary(yearlySummaryParams),
      ]);

      if (dashboardRes.data.success) {
        setDashboardData(dashboardRes.data.data);
      }

      if (yearlySummaryRes.data.success) {
        setYearlySummaryData(yearlySummaryRes.data.data);
      }
    } catch (err) {
      console.error('ダッシュボード取得エラー:', err);
      setError(extractErrorMessage(err, 'ダッシュボードの取得に失敗しました'));
    } finally {
      setLoading(false);
    }
  };

  const fetchUpcomingCases = async () => {
    try {
      const today = new Date();
      const futureDate = new Date();
      futureDate.setMonth(today.getMonth() + 3); // 今後3ヶ月以内

      const params: {
        expected_order_date_from: string;
        expected_order_date_to: string;
        status: string;
        limit: number;
        assigned_employee_id?: string;
      } = {
        expected_order_date_from: `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`,
        expected_order_date_to: `${futureDate.getFullYear()}-${String(futureDate.getMonth() + 1).padStart(2, '0')}-${String(futureDate.getDate()).padStart(2, '0')}`,
        status: 'active',
        limit: 10,
      };

      if (selectedAssignee) {
        params.assigned_employee_id = selectedAssignee;
      }

      const response = await salesCasesApi.getCases(params);

      if (response.data.success) {
        setUpcomingCases(response.data.data.cases || []);
      }
    } catch (err: unknown) {
      console.error('対応が必要な案件取得エラー:', err);
    }
  };

  // アラート案件取得（金額変更・滞留・アクション期限）
  const fetchAlertCases = async () => {
    try {
      const _fmt = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const today = _fmt(new Date());
      const threeDaysLater = _fmt(new Date(Date.now() + 3 * 24 * 60 * 60 * 1000));
      const sevenDaysLater = _fmt(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000));

      // 金額変更アラートを取得
      const amountChangeParams: { days: number; limit: number; assigned_employee_id?: string } = { days: 7, limit: 10 };
      if (selectedAssignee) {
        amountChangeParams.assigned_employee_id = selectedAssignee;
      }
      const amountChangeRes = await salesKpiApi.getAmountChangeAlerts(amountChangeParams);
      if (amountChangeRes.data.success) {
        // フィルタをフロントエンドで適用（APIがassigned_employee_id未対応の場合）
        let alerts = amountChangeRes.data.data.alerts || [];
        if (selectedAssignee) {
          alerts = alerts.filter((a: AmountChangeAlert) => (a as any).assigned_employee_id === selectedAssignee);
        }
        setAmountChangeAlerts(alerts);
      }

      // アクション期限関連案件
      const alertParams: { status: string; limit: number; assigned_employee_id?: string } = {
        status: 'active',
        limit: 50,
      };
      if (selectedAssignee) {
        alertParams.assigned_employee_id = selectedAssignee;
      }
      const overdueRes = await salesCasesApi.getCases(alertParams);

      if (overdueRes.data.success) {
        const cases = overdueRes.data.data.cases || [];
        
        // 期限切れ（過去日）
        const overdue = cases.filter((c: SalesCase) =>
          c.next_action_date && c.next_action_date < today
        );
        setActionOverdueCases(overdue);

        // 3日以内（警告）- todayから3日後まで
        const urgent = cases.filter((c: SalesCase) =>
          c.next_action_date && c.next_action_date >= today && c.next_action_date <= threeDaysLater
        );
        setActionUrgentCases(urgent);

        // 7日以内（注意）- 3日後超〜7日後まで
        const upcoming = cases.filter((c: SalesCase) =>
          c.next_action_date && c.next_action_date > threeDaysLater && c.next_action_date <= sevenDaysLater
        );
        setActionUpcomingCases(upcoming);

        // 滞留案件（days_in_phase > target_days、EXECUTIONフェーズは除外）
        const stagnant = cases.filter((c: SalesCase) =>
          c.sales_phase_code !== 'EXECUTION' &&
          c.days_in_phase && c.target_days &&
          c.days_in_phase > c.target_days
        );
        setStagnationAlertCases(stagnant);
      }
    } catch (err: unknown) {
      console.error('アラート案件取得エラー:', err);
    }
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return '-';
    return formatDateJST(dateString);
  };

  // 失注件数
  const totalLost = lostReasonData?.total_lost || 0;

  // アラート件数合計
  const totalAlerts = amountChangeAlerts.length + stagnationAlertCases.length + actionOverdueCases.length + actionUrgentCases.length + actionUpcomingCases.length;


  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const summary = dashboardData?.summary;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          営業ダッシュボード
        </Typography>
        <Box>
          <Button
            variant="outlined"
            onClick={() => navigate('/sales/cases')}
            sx={{ mr: 1 }}
          >
            案件一覧を見る
          </Button>
          <Button
            variant="contained"
            onClick={() => navigate('/sales/kpi')}
          >
            KPIダッシュボード
          </Button>
        </Box>
      </Box>

      {/* 営業担当者クィックフィルタ */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
            <Person fontSize="small" sx={{ mr: 0.5 }} />
            担当者フィルタ:
          </Typography>
          <Chip
            label="全員"
            variant={selectedAssignee === '' ? 'filled' : 'outlined'}
            color={selectedAssignee === '' ? 'primary' : 'default'}
            onClick={() => setSelectedAssignee('')}
            sx={{ cursor: 'pointer' }}
          />
          {assignees.map((assignee) => (
            <Chip
              key={assignee.employee_id}
              label={assignee.name}
              variant={selectedAssignee === assignee.employee_id ? 'filled' : 'outlined'}
              color={selectedAssignee === assignee.employee_id ? 'primary' : 'default'}
              onClick={() => setSelectedAssignee(assignee.employee_id)}
              sx={{ cursor: 'pointer' }}
            />
          ))}
        </Box>
      </Paper>

      {/* アラートセクション */}
      {totalAlerts > 0 && (
        <Alert
          severity="warning"
          icon={<ErrorOutline />}
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={() => navigate('/sales/cases')}>
              案件一覧へ
            </Button>
          }
        >
          <strong>{totalAlerts}件</strong>の案件に対応が必要です
          {amountChangeAlerts.length > 0 && ` (金額変更: ${amountChangeAlerts.length}件)`}
          {actionOverdueCases.length > 0 && ` (期限切れ: ${actionOverdueCases.length}件)`}
          {actionUrgentCases.length > 0 && ` (3日以内: ${actionUrgentCases.length}件)`}
          {actionUpcomingCases.length > 0 && ` (7日以内: ${actionUpcomingCases.length}件)`}
          {stagnationAlertCases.length > 0 && ` (滞留: ${stagnationAlertCases.length}件)`}
        </Alert>
      )}

      {/* KPIカード（6カード構成） */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {/* 1行目 */}
        <Grid item xs={12} md={4}>
          <Card sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            height: '140px',
          }}>
            <CardContent sx={{
              py: 2,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <AttachMoney sx={{ mr: 1, opacity: 0.8 }} />
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  パイプライン総額
                </Typography>
              </Box>
              <Typography variant="h5" fontWeight="bold">
                {formatCurrencyWithUnit(summary?.total_expected_amount)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            color: 'white',
            height: '140px',
          }}>
            <CardContent sx={{
              py: 2,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <TrendingUp sx={{ mr: 1, opacity: 0.8 }} />
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  フォーキャスト総額
                </Typography>
              </Box>
              <Typography variant="h5" fontWeight="bold">
                {formatCurrencyWithUnit(summary?.forecast_amount)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{
            background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            color: 'white',
            height: '140px',
          }}>
            <CardContent sx={{
              py: 2,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Assignment sx={{ mr: 1, opacity: 0.8 }} />
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  商談中案件数
                </Typography>
              </Box>
              <Typography variant="h5" fontWeight="bold">
                {summary?.total_cases || 0}件
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* 2行目 */}
        <Grid item xs={12} md={4}>
          <Card sx={{
            background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
            color: 'white',
            height: '140px',
          }}>
            <CardContent sx={{
              py: 2,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CheckCircle sx={{ mr: 1, opacity: 0.8 }} />
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    年度内期待売上額
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {fiscalYears.map((year) => (
                    <Chip
                      key={year}
                      label={year}
                      size="small"
                      onClick={() => handleFiscalYearChange(year)}
                      sx={{
                        bgcolor: selectedFiscalYear === year ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.3)',
                        color: selectedFiscalYear === year ? '#11998e' : 'white',
                        fontSize: '0.65rem',
                        height: 20,
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.7)' },
                      }}
                    />
                  ))}
                </Box>
              </Box>
              <Typography variant="h5" fontWeight="bold">
                {formatCurrencyWithUnit(yearlySummaryData?.total_expected_amount)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{
            background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
            color: 'white',
            height: '140px',
          }}>
            <CardContent sx={{
              py: 2,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <AttachMoney sx={{ mr: 1, opacity: 0.8 }} />
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  年度内受注/失注
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-end', flex: 1 }}>
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.65rem' }}>受注</Typography>
                  <Typography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.2 }}>
                    {formatCurrencyWithUnit(yearlySummaryData?.won_amount)}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    {yearlySummaryData?.won_count || 0}件
                  </Typography>
                </Box>
                <Typography variant="h6" sx={{ opacity: 0.6, pb: 1 }}>/</Typography>
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.65rem' }}>失注</Typography>
                  <Typography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.2 }}>
                    {formatCurrencyWithUnit(yearlySummaryData?.lost_amount)}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    {yearlySummaryData?.lost_count || 0}件
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{
            background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
            color: '#333',
            height: '140px',
          }}>
            <CardContent sx={{
              py: 2,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <TrendingUp sx={{ mr: 1, opacity: 0.8 }} />
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  現況パイプライン期待値
                </Typography>
              </Box>
              <Typography variant="h5" fontWeight="bold">
                {formatCurrencyWithUnit(summary?.weighted_expected_amount)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                商談中{summary?.total_cases || 0}件（年度達成率の実績とは別定義）
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* アラート詳細セクション */}
      {(amountChangeAlerts.length > 0 || actionOverdueCases.length > 0 || stagnationAlertCases.length > 0) && (
        <Grid container spacing={2} sx={{ mb: 4 }}>
          {/* 金額変更案件（最優先） */}
          {amountChangeAlerts.length > 0 && (
            <Grid item xs={12} md={6}>
              <Card sx={{ borderLeft: '4px solid #9c27b0' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <AttachMoney sx={{ mr: 1, color: '#9c27b0' }} />
                    <Typography variant="subtitle1" fontWeight="bold">
                      金額変更案件
                    </Typography>
                    <Chip label={`${amountChangeAlerts.length}件`} size="small" sx={{ ml: 1, bgcolor: '#9c27b0', color: 'white' }} />
                  </Box>
                  {amountChangeAlerts.slice(0, 5).map((alert) => (
                    <Box
                      key={alert.case_id}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        py: 1,
                        borderBottom: '1px solid #f0f0f0',
                        '&:last-child': { borderBottom: 'none' },
                        cursor: 'pointer',
                        '&:hover': { bgcolor: '#fafafa' },
                      }}
                      onClick={() => navigate(`/sales/cases/${alert.case_id}`)}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight="medium">{alert.case_name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatCurrencyWithUnit(alert.old_amount)} → {formatCurrencyWithUnit(alert.new_amount)}
                        </Typography>
                      </Box>
                      <Chip
                        label={`${alert.diff_percent > 0 ? '+' : ''}${alert.diff_percent}%`}
                        size="small"
                        color={alert.diff_percent > 0 ? 'success' : 'error'}
                      />
                    </Box>
                  ))}
                  {amountChangeAlerts.length > 5 && (
                    <>
                      <Collapse in={expandedAlerts.amountChange}>
                        {amountChangeAlerts.slice(5).map((alert) => (
                          <Box
                            key={alert.case_id}
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              py: 1,
                              borderBottom: '1px solid #f0f0f0',
                              '&:last-child': { borderBottom: 'none' },
                              cursor: 'pointer',
                              '&:hover': { bgcolor: '#fafafa' },
                            }}
                            onClick={() => navigate(`/sales/cases/${alert.case_id}`)}
                          >
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body2" fontWeight="medium">{alert.case_name}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {formatCurrencyWithUnit(alert.old_amount)} → {formatCurrencyWithUnit(alert.new_amount)}
                              </Typography>
                            </Box>
                            <Chip
                              label={`${alert.diff_percent > 0 ? '+' : ''}${alert.diff_percent}%`}
                              size="small"
                              color={alert.diff_percent > 0 ? 'success' : 'error'}
                            />
                          </Box>
                        ))}
                      </Collapse>
                      <Button
                        size="small"
                        sx={{ mt: 1 }}
                        onClick={() => setExpandedAlerts(prev => ({ ...prev, amountChange: !prev.amountChange }))}
                        endIcon={expandedAlerts.amountChange ? <ExpandLess /> : <ExpandMore />}
                      >
                        {expandedAlerts.amountChange ? '折りたたむ' : `他${amountChangeAlerts.length - 5}件を見る`}
                      </Button>
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* アクション期限切れ（緊急） */}
          {actionOverdueCases.length > 0 && (
            <Grid item xs={12} md={6}>
              <Card sx={{ borderLeft: '4px solid #f44336' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <ErrorOutline color="error" sx={{ mr: 1 }} />
                    <Typography variant="subtitle1" fontWeight="bold">
                      アクション期限切れ
                    </Typography>
                    <Chip label={`${actionOverdueCases.length}件`} size="small" color="error" sx={{ ml: 1 }} />
                  </Box>
                  {actionOverdueCases.slice(0, 5).map((c) => (
                    <Box
                      key={c.case_id}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        py: 1,
                        borderBottom: '1px solid #f0f0f0',
                        '&:last-child': { borderBottom: 'none' },
                        cursor: 'pointer',
                        '&:hover': { bgcolor: '#fafafa' },
                      }}
                      onClick={() => navigate(`/sales/cases/${c.case_id}`)}
                    >
                      <Box>
                        <Typography variant="body2" fontWeight="medium">{c.case_name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          期限: {formatDate(c.next_action_date)} • {c.next_action_memo || '未設定'}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                  {actionOverdueCases.length > 5 && (
                    <>
                      <Collapse in={expandedAlerts.overdue}>
                        {actionOverdueCases.slice(5).map((c) => (
                          <Box
                            key={c.case_id}
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              py: 1,
                              borderBottom: '1px solid #f0f0f0',
                              '&:last-child': { borderBottom: 'none' },
                              cursor: 'pointer',
                              '&:hover': { bgcolor: '#fafafa' },
                            }}
                            onClick={() => navigate(`/sales/cases/${c.case_id}`)}
                          >
                            <Box>
                              <Typography variant="body2" fontWeight="medium">{c.case_name}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                期限: {formatDate(c.next_action_date)} • {c.next_action_memo || '未設定'}
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                      </Collapse>
                      <Button
                        size="small"
                        sx={{ mt: 1 }}
                        onClick={() => setExpandedAlerts(prev => ({ ...prev, overdue: !prev.overdue }))}
                        endIcon={expandedAlerts.overdue ? <ExpandLess /> : <ExpandMore />}
                      >
                        {expandedAlerts.overdue ? '折りたたむ' : `他${actionOverdueCases.length - 5}件を見る`}
                      </Button>
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* アクション期限3日以内（警告） */}
          {actionUrgentCases.length > 0 && (
            <Grid item xs={12} md={6}>
              <Card sx={{ borderLeft: '4px solid #ed6c02' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Warning sx={{ mr: 1, color: '#ed6c02' }} />
                    <Typography variant="subtitle1" fontWeight="bold">
                      アクション期限3日以内
                    </Typography>
                    <Chip label={`${actionUrgentCases.length}件`} size="small" sx={{ ml: 1, bgcolor: '#ed6c02', color: 'white' }} />
                  </Box>
                  {actionUrgentCases.slice(0, 5).map((c) => (
                    <Box
                      key={c.case_id}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        py: 1,
                        borderBottom: '1px solid #f0f0f0',
                        '&:last-child': { borderBottom: 'none' },
                        cursor: 'pointer',
                        '&:hover': { bgcolor: '#fafafa' },
                      }}
                      onClick={() => navigate(`/sales/cases/${c.case_id}`)}
                    >
                      <Box>
                        <Typography variant="body2" fontWeight="medium">{c.case_name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          期限: {formatDate(c.next_action_date)} • {c.next_action_memo || '未設定'}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                  {actionUrgentCases.length > 5 && (
                    <>
                      <Collapse in={expandedAlerts.urgent}>
                        {actionUrgentCases.slice(5).map((c) => (
                          <Box
                            key={c.case_id}
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              py: 1,
                              borderBottom: '1px solid #f0f0f0',
                              '&:last-child': { borderBottom: 'none' },
                              cursor: 'pointer',
                              '&:hover': { bgcolor: '#fafafa' },
                            }}
                            onClick={() => navigate(`/sales/cases/${c.case_id}`)}
                          >
                            <Box>
                              <Typography variant="body2" fontWeight="medium">{c.case_name}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                期限: {formatDate(c.next_action_date)} • {c.next_action_memo || '未設定'}
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                      </Collapse>
                      <Button
                        size="small"
                        sx={{ mt: 1 }}
                        onClick={() => setExpandedAlerts(prev => ({ ...prev, urgent: !prev.urgent }))}
                        endIcon={expandedAlerts.urgent ? <ExpandLess /> : <ExpandMore />}
                      >
                        {expandedAlerts.urgent ? '折りたたむ' : `他${actionUrgentCases.length - 5}件を見る`}
                      </Button>
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* アクション期限7日以内（注意） */}
          {actionUpcomingCases.length > 0 && (
            <Grid item xs={12} md={6}>
              <Card sx={{ borderLeft: '4px solid #0288d1' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Schedule sx={{ mr: 1, color: '#0288d1' }} />
                    <Typography variant="subtitle1" fontWeight="bold">
                      アクション期限7日以内
                    </Typography>
                    <Chip label={`${actionUpcomingCases.length}件`} size="small" sx={{ ml: 1, bgcolor: '#0288d1', color: 'white' }} />
                  </Box>
                  {actionUpcomingCases.slice(0, 5).map((c) => (
                    <Box
                      key={c.case_id}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        py: 1,
                        borderBottom: '1px solid #f0f0f0',
                        '&:last-child': { borderBottom: 'none' },
                        cursor: 'pointer',
                        '&:hover': { bgcolor: '#fafafa' },
                      }}
                      onClick={() => navigate(`/sales/cases/${c.case_id}`)}
                    >
                      <Box>
                        <Typography variant="body2" fontWeight="medium">{c.case_name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          期限: {formatDate(c.next_action_date)} • {c.next_action_memo || '未設定'}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                  {actionUpcomingCases.length > 5 && (
                    <>
                      <Collapse in={expandedAlerts.upcoming}>
                        {actionUpcomingCases.slice(5).map((c) => (
                          <Box
                            key={c.case_id}
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              py: 1,
                              borderBottom: '1px solid #f0f0f0',
                              '&:last-child': { borderBottom: 'none' },
                              cursor: 'pointer',
                              '&:hover': { bgcolor: '#fafafa' },
                            }}
                            onClick={() => navigate(`/sales/cases/${c.case_id}`)}
                          >
                            <Box>
                              <Typography variant="body2" fontWeight="medium">{c.case_name}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                期限: {formatDate(c.next_action_date)} • {c.next_action_memo || '未設定'}
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                      </Collapse>
                      <Button
                        size="small"
                        sx={{ mt: 1 }}
                        onClick={() => setExpandedAlerts(prev => ({ ...prev, upcoming: !prev.upcoming }))}
                        endIcon={expandedAlerts.upcoming ? <ExpandLess /> : <ExpandMore />}
                      >
                        {expandedAlerts.upcoming ? '折りたたむ' : `他${actionUpcomingCases.length - 5}件を見る`}
                      </Button>
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* 滞留案件 */}
          {stagnationAlertCases.length > 0 && (
            <Grid item xs={12} md={6}>
              <Card sx={{ borderLeft: '4px solid #ff9800' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Schedule color="warning" sx={{ mr: 1 }} />
                    <Typography variant="subtitle1" fontWeight="bold">
                      滞留案件
                    </Typography>
                    <Chip label={`${stagnationAlertCases.length}件`} size="small" color="warning" sx={{ ml: 1 }} />
                  </Box>
                  {stagnationAlertCases.slice(0, 5).map((c) => (
                    <Box
                      key={c.case_id}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        py: 1,
                        borderBottom: '1px solid #f0f0f0',
                        '&:last-child': { borderBottom: 'none' },
                        cursor: 'pointer',
                        '&:hover': { bgcolor: '#fafafa' },
                      }}
                      onClick={() => navigate(`/sales/cases/${c.case_id}`)}
                    >
                      <Box>
                        <Typography variant="body2" fontWeight="medium">{c.case_name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {c.sales_phase_name} • {c.days_in_phase}日滞留（目標{c.target_days}日）
                        </Typography>
                      </Box>
                      <Chip
                        label={`+${(c.days_in_phase || 0) - (c.target_days || 0)}日`}
                        size="small"
                        color="error"
                      />
                    </Box>
                  ))}
                  {stagnationAlertCases.length > 5 && (
                    <>
                      <Collapse in={expandedAlerts.stagnation}>
                        {stagnationAlertCases.slice(5).map((c) => (
                          <Box
                            key={c.case_id}
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              py: 1,
                              borderBottom: '1px solid #f0f0f0',
                              '&:last-child': { borderBottom: 'none' },
                              cursor: 'pointer',
                              '&:hover': { bgcolor: '#fafafa' },
                            }}
                            onClick={() => navigate(`/sales/cases/${c.case_id}`)}
                          >
                            <Box>
                              <Typography variant="body2" fontWeight="medium">{c.case_name}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {c.sales_phase_name} • {c.days_in_phase}日滞留（目標{c.target_days}日）
                              </Typography>
                            </Box>
                            <Chip
                              label={`+${(c.days_in_phase || 0) - (c.target_days || 0)}日`}
                              size="small"
                              color="error"
                            />
                          </Box>
                        ))}
                      </Collapse>
                      <Button
                        size="small"
                        sx={{ mt: 1 }}
                        onClick={() => setExpandedAlerts(prev => ({ ...prev, stagnation: !prev.stagnation }))}
                        endIcon={expandedAlerts.stagnation ? <ExpandLess /> : <ExpandMore />}
                      >
                        {expandedAlerts.stagnation ? '折りたたむ' : `他${stagnationAlertCases.length - 5}件を見る`}
                      </Button>
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      )}

      <Divider sx={{ my: 3 }} />

      {/* 話題ヒットマップ（事業カテゴリ別） */}
      <Typography variant="h6" sx={{ mb: 2 }}>
        話題ヒットマップ（事業カテゴリ別）
      </Typography>
      <Card sx={{ mb: 4 }}>
        <CardContent>
          {heatmapData && heatmapData.length > 0 ? (
            <Grid container spacing={2}>
              {heatmapData.map((item) => {
                const maxAmount = Math.max(...heatmapData.map(d => d.total_amount), 1);
                const intensity = item.total_amount / maxAmount;
                const backgroundColor = intensity > 0
                  ? `rgba(25, 118, 210, ${0.2 + (intensity * 0.6)})`
                  : '#f5f5f5';

                return (
                  <Grid item xs={6} md={3} key={item.category}>
                    <Card sx={{
                      bgcolor: backgroundColor,
                      border: '1px solid #e0e0e0',
                      transition: 'transform 0.2s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 3,
                      }
                    }}>
                      <CardContent>
                        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                          {item.category}
                        </Typography>
                        <Typography variant="h5" fontWeight="bold" color="primary">
                          {item.case_count}件
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {formatCurrencyWithUnit(item.total_amount)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary">
                事業カテゴリが設定された案件がありません
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* 3ヶ月トピック・受注予定額推移を横並び */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* 3ヶ月トピック */}
        <Grid item xs={12} md={6}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            3ヶ月トピック
          </Typography>
          <Card>
            <CardContent>
              {upcomingCases.length > 0 ? (
                upcomingCases.slice(0, 5).map((salesCase) => (
                  <Box
                    key={salesCase.case_id}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      py: 1,
                      borderBottom: '1px solid #f0f0f0',
                      '&:last-child': { borderBottom: 'none' },
                      cursor: 'pointer',
                      '&:hover': { bgcolor: '#fafafa' },
                    }}
                    onClick={() => navigate(`/sales/cases/${salesCase.case_id}`)}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" fontWeight="medium">{salesCase.case_name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(salesCase.expected_order_date)} • {formatCurrencyWithUnit(salesCase.expected_order_amount)}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        bgcolor: salesCase.phase_color_code || '#e0e0e0',
                        fontSize: '0.75rem',
                      }}
                    >
                      {salesCase.sales_phase_name || '-'}
                    </Box>
                  </Box>
                ))
              ) : (
                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                  今週受注予定の案件はありません
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* 受注予定額の推移（月次） */}
        <Grid item xs={12} md={6}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            受注予定額の推移（月次）
          </Typography>
          <Card>
            <CardContent>
              {dashboardData?.by_month && dashboardData.by_month.length > 0 ? (
                <Box>
                  {dashboardData.by_month.map((monthData) => (
                    <Box
                      key={monthData.month}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        py: 1,
                        borderBottom: '1px solid #e0e0e0',
                        '&:last-child': { borderBottom: 'none' },
                      }}
                    >
                      <Typography variant="body2">{monthData.month}</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {formatCurrencyWithUnit(monthData.total_expected_amount)} ({monthData.case_count}件)
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <TrendingUp sx={{ fontSize: 60, color: '#ccc', mb: 1 }} />
                  <Typography color="text.secondary">
                    月次データがありません。案件を登録すると表示されます。
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* 顧客別統計 */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          顧客別統計
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {fiscalYears.map((year) => (
            <Chip
              key={year}
              label={year}
              size="small"
              onClick={() => handleAccountStatsYearChange(year)}
              sx={{
                bgcolor: accountStatsYear === year ? 'primary.main' : 'grey.200',
                color: accountStatsYear === year ? 'white' : 'text.primary',
                cursor: 'pointer',
                '&:hover': { bgcolor: accountStatsYear === year ? 'primary.dark' : 'grey.300' },
              }}
            />
          ))}
        </Box>
      </Box>
      <Card sx={{ mb: 4 }}>
        <CardContent>
          {accountStatsData?.accounts && accountStatsData.accounts.length > 0 ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 40 }}></TableCell>
                    <TableCell>顧客名</TableCell>
                    <TableCell align="right">案件数</TableCell>
                    <TableCell align="right">社員/BP人月</TableCell>
                    <TableCell align="right">確定売上</TableCell>
                    <TableCell align="right">確定外注費</TableCell>
                    <TableCell align="right">確定利益</TableCell>
                    <TableCell align="right">話題期待売上</TableCell>
                    <TableCell align="right">話題期待外注費</TableCell>
                    <TableCell align="right">話題期待利益</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {accountStatsData.accounts.map((account) => (
                    <React.Fragment key={account.account_id}>
                      <TableRow
                        hover
                        sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#f5f5f5' } }}
                        onClick={() => setExpandedAccount(expandedAccount === account.account_id ? null : account.account_id)}
                      >
                        <TableCell>
                          <IconButton size="small">
                            {expandedAccount === account.account_id ? <ExpandLess /> : <ExpandMore />}
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Business fontSize="small" color="action" />
                            {account.account_name}
                          </Box>
                        </TableCell>
                        <TableCell align="right">{account.case_count}件</TableCell>
                        <TableCell align="right">{account.total_man_months.toFixed(1)}</TableCell>
                        <TableCell align="right">{formatCurrencyWithUnit(account.confirmed_sales)}</TableCell>
                        <TableCell align="right">{formatCurrencyWithUnit(account.confirmed_outsource_cost)}</TableCell>
                        <TableCell align="right">{formatCurrencyWithUnit(account.confirmed_profit)}</TableCell>
                        <TableCell align="right">{formatCurrencyWithUnit(account.expected_sales)}</TableCell>
                        <TableCell align="right">{formatCurrencyWithUnit(account.expected_outsource_cost)}</TableCell>
                        <TableCell align="right">{formatCurrencyWithUnit(account.expected_profit)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={10} sx={{ p: 0, border: 0 }}>
                          <Collapse in={expandedAccount === account.account_id} timeout="auto" unmountOnExit>
                            <Box sx={{ m: 2, bgcolor: '#f5f5f5', borderRadius: 1, p: 2 }}>
                              <Typography variant="subtitle2" sx={{ mb: 1 }}>案件一覧</Typography>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell sx={{ width: 40 }}></TableCell>
                                    <TableCell>案件名</TableCell>
                                    <TableCell>フェーズ</TableCell>
                                    <TableCell>期間</TableCell>
                                    <TableCell align="right">社員/BP人月</TableCell>
                                    <TableCell align="right">確定売上</TableCell>
                                    <TableCell align="right">確定外注費</TableCell>
                                    <TableCell align="right">確定利益</TableCell>
                                    <TableCell align="right">話題期待売上</TableCell>
                                    <TableCell align="right">話題期待外注費</TableCell>
                                    <TableCell align="right">話題期待利益</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {account.cases.map((caseItem) => (
                                    <React.Fragment key={caseItem.case_id}>
                                      <TableRow
                                        hover
                                        sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#e8e8e8' } }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setExpandedCase(expandedCase === caseItem.case_id ? null : caseItem.case_id);
                                        }}
                                      >
                                        <TableCell>
                                          <IconButton size="small">
                                            {expandedCase === caseItem.case_id ? <ExpandLess /> : <ExpandMore />}
                                          </IconButton>
                                        </TableCell>
                                        <TableCell>
                                          <Typography variant="body2" fontWeight="medium">
                                            {caseItem.case_name}
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary">
                                            {caseItem.case_no}
                                          </Typography>
                                        </TableCell>
                                        <TableCell>
                                          <Chip label={caseItem.phase_name || '-'} size="small" />
                                        </TableCell>
                                        <TableCell>
                                          {caseItem.start_month && caseItem.end_month 
                                            ? `${caseItem.start_month}〜${caseItem.end_month}`
                                            : '-'
                                          }
                                        </TableCell>
                                        <TableCell align="right">{caseItem.man_months.toFixed(1)}</TableCell>
                                        <TableCell align="right">{formatCurrencyWithUnit(caseItem.confirmed_sales)}</TableCell>
                                        <TableCell align="right">{formatCurrencyWithUnit(caseItem.confirmed_outsource_cost)}</TableCell>
                                        <TableCell align="right">{formatCurrencyWithUnit(caseItem.confirmed_profit)}</TableCell>
                                        <TableCell align="right">{formatCurrencyWithUnit(caseItem.expected_sales)}</TableCell>
                                        <TableCell align="right">{formatCurrencyWithUnit(caseItem.expected_outsource_cost)}</TableCell>
                                        <TableCell align="right">{formatCurrencyWithUnit(caseItem.expected_profit)}</TableCell>
                                      </TableRow>
                                      <TableRow>
                                        <TableCell colSpan={11} sx={{ p: 0, border: 0 }}>
                                          <Collapse in={expandedCase === caseItem.case_id} timeout="auto" unmountOnExit>
                                            <Box sx={{ m: 2, ml: 6, bgcolor: '#fff', borderRadius: 1, p: 2, border: '1px solid #e0e0e0' }}>
                                              <Typography variant="caption" fontWeight="bold" sx={{ mb: 1, display: 'block' }}>月別詳細</Typography>
                                              <Table size="small">
                                                <TableHead>
                                                  <TableRow>
                                                    <TableCell>月</TableCell>
                                                    <TableCell align="right">社員/BP人月</TableCell>
                                                    <TableCell align="right">確定売上</TableCell>
                                                    <TableCell align="right">確定外注費</TableCell>
                                                    <TableCell align="right">確定利益</TableCell>
                                                    <TableCell align="right">話題期待売上</TableCell>
                                                    <TableCell align="right">話題期待外注費</TableCell>
                                                    <TableCell align="right">話題期待利益</TableCell>
                                                  </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                  {caseItem.monthly.length > 0 ? (
                                                    caseItem.monthly.map((m) => (
                                                      <TableRow key={m.month}>
                                                        <TableCell>{m.month}</TableCell>
                                                        <TableCell align="right">{m.man_months.toFixed(1)}</TableCell>
                                                        <TableCell align="right">{formatCurrencyWithUnit(m.confirmed_sales)}</TableCell>
                                                        <TableCell align="right">{formatCurrencyWithUnit(m.confirmed_outsource_cost)}</TableCell>
                                                        <TableCell align="right">{formatCurrencyWithUnit(m.confirmed_profit)}</TableCell>
                                                        <TableCell align="right">{formatCurrencyWithUnit(m.expected_sales)}</TableCell>
                                                        <TableCell align="right">{formatCurrencyWithUnit(m.expected_outsource_cost)}</TableCell>
                                                        <TableCell align="right">{formatCurrencyWithUnit(m.expected_profit)}</TableCell>
                                                      </TableRow>
                                                    ))
                                                  ) : (
                                                    <TableRow>
                                                      <TableCell colSpan={8} align="center">
                                                        <Typography variant="caption" color="text.secondary">
                                                          月別データなし
                                                        </Typography>
                                                      </TableCell>
                                                    </TableRow>
                                                  )}
                                                </TableBody>
                                              </Table>
                                            </Box>
                                          </Collapse>
                                        </TableCell>
                                      </TableRow>
                                    </React.Fragment>
                                  ))}
                                </TableBody>
                              </Table>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Business sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
              <Typography color="text.secondary">
                顧客別統計データがありません
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* パイプライン分析 */}
      <Typography variant="h6" sx={{ mb: 2 }}>
        パイプライン分析
      </Typography>
      <Card sx={{ mb: 4 }}>
        <CardContent>
          {pipelineData?.phases && pipelineData.phases.length > 0 ? (
            <Box>
              {pipelineData.phases.map((phase) => {
                const maxAmount = Math.max(...pipelineData.phases.map(p => p.total_expected_amount));
                const widthPercent = maxAmount > 0 ? (phase.total_expected_amount / maxAmount) * 100 : 0;
                return (
                  <Box key={phase.phase_code} sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ minWidth: 100, fontSize: '0.8rem' }}>{phase.phase_name}</Typography>
                    <Box sx={{ flex: 1, position: 'relative' }}>
                      <Box
                        sx={{
                          height: 24,
                          bgcolor: phase.color_code || '#1976d2',
                          width: `${Math.max(widthPercent, 5)}%`,
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Typography variant="caption" sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.7rem' }}>
                          {phase.case_count}件
                        </Typography>
                      </Box>
                    </Box>
                    <Typography variant="caption" sx={{ minWidth: 80, textAlign: 'right' }}>
                      {formatCurrencyWithUnit(phase.total_expected_amount)}
                    </Typography>
                  </Box>
                );
              })}
              <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="caption">合計</Typography>
                <Typography variant="caption" fontWeight="bold">
                  {pipelineData.summary.total_active_cases}件
                </Typography>
              </Box>
            </Box>
          ) : (
            <Typography color="text.secondary">パイプラインデータがありません</Typography>
          )}
        </CardContent>
      </Card>

      {/* フェーズ遷移分析（サンキーダイアグラム） */}
      <Box sx={{ mb: 4 }}>
        <PhaseTransitionSankey employeeId={selectedAssignee} />
      </Box>

      {/* 失注理由分析 */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          失注理由分析
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            variant={lostReasonYear === null ? 'contained' : 'outlined'}
            onClick={() => handleYearChange(null)}
          >
            全期間
          </Button>
          <Button
            size="small"
            variant={lostReasonYear === 2025 ? 'contained' : 'outlined'}
            onClick={() => handleYearChange(2025)}
          >
            2025年
          </Button>
          <Button
            size="small"
            variant={lostReasonYear === 2024 ? 'contained' : 'outlined'}
            onClick={() => handleYearChange(2024)}
          >
            2024年
          </Button>
        </Box>
      </Box>
      <Card>
        <CardContent>
          {lostReasonData?.by_reason && lostReasonData.by_reason.length > 0 ? (
            <Grid container spacing={3}>
              {/* カテゴリ別（横棒グラフ） */}
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
                  カテゴリ別分布
                </Typography>
                {lostReasonData.by_category?.map(cat => {
                  const maxCount = Math.max(...(lostReasonData.by_category?.map(c => c.count) || [1]));
                  const percentage = (cat.count / maxCount) * 100;
                  return (
                    <Box key={cat.category} sx={{ mb: 1.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2">{cat.category}</Typography>
                        <Typography variant="body2" fontWeight="bold">{cat.count}件</Typography>
                      </Box>
                      <Box sx={{ height: 8, bgcolor: '#f0f0f0', borderRadius: 1, overflow: 'hidden' }}>
                        <Box
                          sx={{
                            height: '100%',
                            width: `${percentage}%`,
                            bgcolor: '#f44336',
                            borderRadius: 1,
                            transition: 'width 0.3s',
                          }}
                        />
                      </Box>
                    </Box>
                  );
                })}
              </Grid>

              {/* 理由別TOP5 */}
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
                  失注理由TOP5
                </Typography>
                {lostReasonData.by_reason.slice(0, 5).map((reason, index) => (
                  <Box
                    key={reason.reason_code}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      py: 1,
                      borderBottom: index < 4 ? '1px solid #f0f0f0' : 'none',
                    }}
                  >
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        bgcolor: index === 0 ? '#f44336' : index === 1 ? '#ff9800' : '#e0e0e0',
                        color: index < 2 ? 'white' : 'text.secondary',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.75rem',
                        fontWeight: 'bold',
                        mr: 1.5,
                      }}
                    >
                      {index + 1}
                    </Box>
                    <Typography variant="body2" sx={{ flex: 1 }}>{reason.reason_name}</Typography>
                    <Chip label={`${reason.count}件`} size="small" color={index === 0 ? 'error' : 'default'} />
                  </Box>
                ))}
              </Grid>

              {/* フェーズ別失注 */}
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
                  失注フェーズ分布
                </Typography>
                {lostReasonData.by_phase?.map((phase, index) => (
                  <Box
                    key={phase.phase_id}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      py: 1,
                      borderBottom: index < (lostReasonData.by_phase?.length || 0) - 1 ? '1px solid #f0f0f0' : 'none',
                    }}
                  >
                    <Typography variant="body2">{phase.phase_name}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" fontWeight="bold">{phase.lost_count}件</Typography>
                      <Typography variant="caption" color="text.secondary">({phase.percentage}%)</Typography>
                    </Box>
                  </Box>
                )) || <Typography variant="body2" color="text.secondary">データなし</Typography>}
              </Grid>

              {/* 合計 */}
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    失注合計: <strong>{lostReasonData.total_lost}件</strong>
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CheckCircle sx={{ fontSize: 48, color: '#4caf50', mb: 1 }} />
              <Typography color="text.secondary">失注データがありません</Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default SalesDashboardPage;
